import type { ChatSubagentRun, ChatWorkflowRun } from "@/lib/chat";
import { groupSubagentRunsByPhase, splitWorkflowRuns } from "@/lib/subagent-tasks";
import { SubagentPhaseSection } from "./SubagentPhaseSection";
import { SubagentSectionHeader, SubagentSectionToggle } from "./SubagentSectionHeader";
import { SubagentTaskCardList } from "./SubagentTaskCard";
import { WorkflowTaskCard } from "./WorkflowTaskCard";
import { useSectionCollapse } from "./useSectionCollapse";

export function SubagentTaskList({
	runs,
	workflowRuns,
	selectedId,
	onSelect,
}: {
	runs: ChatSubagentRun[];
	/** workflow はカード 1 枚で描く。所有する run はそのカードの中にだけ出る。 */
	workflowRuns: readonly ChatWorkflowRun[];
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	const { phases, running, finished } = groupSubagentRunsByPhase(runs, workflowRuns);
	const workflows = splitWorkflowRuns(workflowRuns);
	const selectedIsFinished =
		finished.some((run) => run.id === selectedId) ||
		workflows.finished.some((workflow) =>
			runs.some((run) => run.workflowRunId === workflow.id && run.id === selectedId),
		);
	const [finishedCollapsed, toggleFinished] = useSectionCollapse(
		!selectedIsFinished,
		selectedIsFinished ? selectedId : null,
	);
	const workflowCards = (list: readonly ChatWorkflowRun[]) =>
		list.map((workflow) => (
			<WorkflowTaskCard
				key={workflow.id}
				workflow={workflow}
				runs={runs}
				selectedId={selectedId}
				onSelect={onSelect}
			/>
		));
	return (
		<div style={{ height: "100%", overflowY: "auto", padding: "8px 0" }}>
			{phases.map((phase) => (
				<SubagentPhaseSection
					key={phase.key}
					phase={phase}
					selectedId={selectedId}
					onSelect={onSelect}
				/>
			))}
			{(running.length > 0 || workflows.running.length > 0) && (
				<section>
					{/* 実行中は畳む意味が無い (今見るべきものがここにしか無い) ので chevron を出さない。 */}
					<SubagentSectionHeader label="実行中" count={workflows.running.length + running.length} />
					{workflowCards(workflows.running)}
					<SubagentTaskCardList runs={running} selectedId={selectedId} onSelect={onSelect} />
				</section>
			)}
			{(finished.length > 0 || workflows.finished.length > 0) && (
				<section>
					<SubagentSectionToggle
						testId="subagent-finished-toggle"
						label="実行済み"
						count={workflows.finished.length + finished.length}
						collapsed={finishedCollapsed}
						onToggle={toggleFinished}
					/>
					{!finishedCollapsed && (
						<>
							{workflowCards(workflows.finished)}
							<SubagentTaskCardList runs={finished} selectedId={selectedId} onSelect={onSelect} />
						</>
					)}
				</section>
			)}
		</div>
	);
}
